import { useState, useMemo, useEffect, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Sparkles, Lightbulb, ArrowUp, MessageSquare, Pin, Calendar, CheckCircle2,
  Hammer, ListChecks, XCircle, Plus, Inbox, Filter, ExternalLink, Trash2, Pencil, Loader2,
} from "lucide-react";

import {
  fetchReleases, markReleaseViewed,
  fetchIdeas, createIdea, voteIdea, unvoteIdea,
  adminUpdateIdea, adminDeleteIdea,
} from "@/lib/whatsNewApi";

const CATEGORY_STYLE = {
  feature:      { label: "Feature",      bg: "bg-emerald-100",   text: "text-emerald-800" },
  improvement:  { label: "Improvement",  bg: "bg-blue-100",      text: "text-blue-800" },
  fix:          { label: "Fix",          bg: "bg-amber-100",     text: "text-amber-800" },
  announcement: { label: "Announcement", bg: "bg-violet-100",    text: "text-violet-800" },
};

const STATUS_META = {
  pending_approval: { label: "Pending review", bg: "bg-orange-100",  text: "text-orange-800",  icon: Inbox },
  submitted:        { label: "Submitted",      bg: "bg-slate-100",   text: "text-slate-700",   icon: Inbox },
  planned:          { label: "Planned",        bg: "bg-blue-100",    text: "text-blue-800",    icon: ListChecks },
  in_progress:      { label: "In Progress",    bg: "bg-amber-100",   text: "text-amber-800",   icon: Hammer },
  shipped:          { label: "Shipped",        bg: "bg-emerald-100", text: "text-emerald-800", icon: CheckCircle2 },
  declined:         { label: "Declined",       bg: "bg-red-50",      text: "text-red-700",     icon: XCircle },
};

const IDEA_CATEGORIES = ["leads","occupancy","reports","integrations","admin","mobile","other"];

function fmtDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function getCachedUser() {
  try { return JSON.parse(localStorage.getItem("cachedUser") || "{}"); } catch { return {}; }
}

// ──────────────────────────────────────────────────────────────────────
// RELEASE CARD
// ──────────────────────────────────────────────────────────────────────
function ReleaseCard({ release, isUnread, onMarkRead }) {
  const cat = CATEGORY_STYLE[release.category] || CATEGORY_STYLE.feature;
  return (
    <Card
      id={`release-${release.slug}`}
      className={`border-0 shadow-md overflow-hidden transition-all ${isUnread ? "ring-2 ring-cyan-300 bg-gradient-to-br from-cyan-50/50 to-white" : "bg-white"}`}
      data-testid={`release-card-${release.slug}`}
    >
      <CardContent className="p-5">
        <div className="flex items-start gap-3 mb-3 flex-wrap">
          {release.pinned && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-800 text-[10px] font-bold uppercase tracking-wider">
              <Pin className="h-3 w-3" /> Pinned
            </span>
          )}
          <span className={`inline-flex items-center px-2 py-0.5 rounded-full ${cat.bg} ${cat.text} text-[10px] font-bold uppercase tracking-wider`}>
            {cat.label}
          </span>
          {isUnread && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-cyan-600 text-white text-[10px] font-bold uppercase tracking-wider">
              New
            </span>
          )}
          <span className="text-xs text-gray-400 ml-auto inline-flex items-center gap-1">
            <Calendar className="h-3 w-3" /> {fmtDate(release.published_at)}
          </span>
        </div>
        <h3 className="text-xl font-extrabold text-gray-900 leading-tight" style={{ fontFamily: "Manrope" }}>
          {release.title}
        </h3>
        <p className="text-sm text-gray-700 mt-2 leading-relaxed whitespace-pre-line">{release.body}</p>

        {release.bullets?.length > 0 && (
          <ul className="mt-3 space-y-1.5">
            {release.bullets.map((b, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                <span className="text-cyan-500 mt-1.5 leading-none">•</span>
                <span>{b}</span>
              </li>
            ))}
          </ul>
        )}

        {release.linked_idea_titles?.length > 0 && (
          <div className="mt-4 px-3 py-2 rounded-md bg-emerald-50 border border-emerald-200">
            <div className="text-[11px] font-bold text-emerald-800 uppercase tracking-wider mb-1">
              You asked, we shipped
            </div>
            <ul className="space-y-0.5">
              {release.linked_idea_titles.map((t, i) => (
                <li key={i} className="text-xs text-emerald-900 flex items-center gap-1.5">
                  <CheckCircle2 className="h-3 w-3 flex-shrink-0" /> {t}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="mt-4 flex items-center justify-between gap-2 pt-3 border-t border-gray-100">
          <span className="text-xs text-gray-400">Posted by {release.published_by}</span>
          {isUnread && (
            <Button size="sm" variant="outline" onClick={() => onMarkRead(release.id)} className="h-7 text-xs" data-testid={`mark-read-${release.slug}`}>
              Got it
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// ──────────────────────────────────────────────────────────────────────
// IDEA CARD
// ──────────────────────────────────────────────────────────────────────
function IdeaCard({ idea, onToggleVote, canModerate, onEdit, onDelete, onApprove }) {
  const status = STATUS_META[idea.status] || STATUS_META.submitted;
  const StatusIcon = status.icon;
  const isPending = idea.status === "pending_approval";

  return (
    <Card className="border-0 shadow-sm hover:shadow-md transition-shadow bg-white group" data-testid={`idea-card-${idea.id}`}>
      <CardContent className="p-4">
        <div className="flex gap-3">
          {/* Vote column */}
          <button
            onClick={() => !isPending && onToggleVote(idea)}
            disabled={isPending}
            className={`flex flex-col items-center justify-center px-3 py-2 rounded-lg border-2 transition-all flex-shrink-0 min-w-[60px]
              ${isPending ? "border-gray-100 bg-gray-50 text-gray-300 cursor-not-allowed"
                : idea.did_vote ? "border-cyan-500 bg-cyan-50 text-cyan-700"
                : "border-gray-200 bg-white text-gray-600 hover:border-cyan-300 hover:bg-cyan-50/50"}`}
            data-testid={`vote-btn-${idea.id}`}
            title={isPending ? "Awaiting approval — votes open once published" : (idea.did_vote ? "Click to remove your +1" : "+1 this idea")}
          >
            <ArrowUp className={`h-4 w-4 ${idea.did_vote ? "fill-current" : ""}`} />
            <span className="text-base font-extrabold leading-tight tabular-nums">{idea.vote_count || 0}</span>
          </button>

          {/* Body */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start gap-2 flex-wrap mb-1">
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full ${status.bg} ${status.text} text-[10px] font-bold uppercase tracking-wider`}>
                <StatusIcon className="h-3 w-3" />
                {status.label}
              </span>
              <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 text-[10px] font-semibold uppercase tracking-wider">
                {idea.category}
              </span>
              {idea.linked_release_title && (
                <span className="inline-flex items-center gap-1 text-[10px] text-emerald-700 font-semibold">
                  <ExternalLink className="h-3 w-3" /> Released {fmtDate(idea.shipped_at)}
                </span>
              )}
              {canModerate && (
                <div className="ml-auto flex items-center gap-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
                  {isPending && (
                    <button
                      onClick={() => onApprove(idea)}
                      className="inline-flex items-center justify-center h-6 px-2 text-[10px] font-bold uppercase tracking-wider rounded-md bg-emerald-600 text-white hover:bg-emerald-700 transition-colors"
                      title="Approve and publish"
                      data-testid={`approve-idea-${idea.id}`}
                    >
                      Approve
                    </button>
                  )}
                  <button
                    onClick={() => onEdit(idea)}
                    className="inline-flex items-center justify-center h-6 w-6 rounded-md text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                    title="Edit (Super Admin)"
                    data-testid={`edit-idea-${idea.id}`}
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                  <button
                    onClick={() => onDelete(idea)}
                    className="inline-flex items-center justify-center h-6 w-6 rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                    title="Delete (Super Admin)"
                    data-testid={`delete-idea-${idea.id}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              )}
            </div>
            <h3 className="text-sm font-bold text-gray-900 leading-snug">{idea.title}</h3>
            <p className="text-xs text-gray-600 mt-1 leading-relaxed line-clamp-3 whitespace-pre-line">{idea.description}</p>

            <div className="mt-2 flex items-center gap-3 text-[11px] text-gray-500 flex-wrap">
              <span className="font-medium">{idea.author_name || "Anonymous"}</span>
              {(idea.author_tenant || idea.author_location) && (
                <>
                  <span className="text-gray-400">·</span>
                  <span>{[idea.author_tenant, idea.author_location].filter(Boolean).join(" · ")}</span>
                </>
              )}
              <span className="text-gray-400">·</span>
              <span>{fmtDate(idea.created_at)}</span>
              {idea.comment_count > 0 && (
                <>
                  <span className="text-gray-400">·</span>
                  <span className="inline-flex items-center gap-1">
                    <MessageSquare className="h-3 w-3" /> {idea.comment_count}
                  </span>
                </>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ──────────────────────────────────────────────────────────────────────
// EDIT IDEA DIALOG (super-admin)
// ──────────────────────────────────────────────────────────────────────
function EditIdeaDialog({ idea, open, onClose, onSaved }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (idea) {
      setForm({
        title: idea.title || "",
        description: idea.description || "",
        category: idea.category || "other",
        status: idea.status || "submitted",
        decline_reason: idea.decline_reason || "",
      });
    }
  }, [idea]);

  if (!idea) return null;

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (form.status !== "declined") delete payload.decline_reason;
      const updated = await adminUpdateIdea(idea.id, payload);
      toast.success("Idea updated");
      onSaved(updated);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update idea");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="edit-idea-dialog">
        <DialogHeader>
          <DialogTitle>Edit idea</DialogTitle>
          <DialogDescription>Super-admin only — edits are visible to everyone immediately.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label htmlFor="edit-title">Title</Label>
            <Input id="edit-title" value={form.title || ""} onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))} data-testid="edit-idea-title" />
          </div>
          <div>
            <Label htmlFor="edit-desc">Description</Label>
            <Textarea id="edit-desc" rows={4} value={form.description || ""} onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))} data-testid="edit-idea-desc" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="edit-category">Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm(f => ({ ...f, category: v }))}>
                <SelectTrigger id="edit-category" data-testid="edit-idea-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {IDEA_CATEGORIES.map(c => <SelectItem key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="edit-status">Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm(f => ({ ...f, status: v }))}>
                <SelectTrigger id="edit-status" data-testid="edit-idea-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(STATUS_META).map(([k, m]) => <SelectItem key={k} value={k}>{m.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          {form.status === "declined" && (
            <div>
              <Label htmlFor="edit-decline">Decline reason (visible publicly)</Label>
              <Textarea id="edit-decline" rows={2} value={form.decline_reason || ""} onChange={(e) => setForm(f => ({ ...f, decline_reason: e.target.value }))} placeholder="Brief, friendly explanation — keeps trust." data-testid="edit-idea-decline" />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white" data-testid="edit-idea-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Save changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ──────────────────────────────────────────────────────────────────────
// MAIN
// ──────────────────────────────────────────────────────────────────────
export default function WhatsNew() {
  const [tab, setTab] = useState("releases");
  const [filterStatus, setFilterStatus] = useState("all");
  const [sort, setSort] = useState("top");
  const [submitOpen, setSubmitOpen] = useState(false);
  const [editingIdea, setEditingIdea] = useState(null);

  const [releases, setReleases] = useState([]);
  const [ideas, setIdeas] = useState([]);
  const [loadingReleases, setLoadingReleases] = useState(true);
  const [loadingIdeas, setLoadingIdeas] = useState(true);

  // Track viewed release IDs for the "New" badge — driven by /unread-count count vs released list
  const [viewedReleaseIds, setViewedReleaseIds] = useState(() => new Set());

  const isSuperAdmin = useMemo(() => getCachedUser()?.role === "super_admin", []);

  // ── Data loaders ─────────────────────────────────────────────────────
  const loadReleases = useCallback(async () => {
    setLoadingReleases(true);
    try {
      const data = await fetchReleases();
      setReleases(data);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load release notes");
    } finally {
      setLoadingReleases(false);
    }
  }, []);

  const loadIdeas = useCallback(async () => {
    setLoadingIdeas(true);
    try {
      const data = await fetchIdeas({ status: filterStatus, sort, includePending: isSuperAdmin });
      setIdeas(data);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load ideas");
    } finally {
      setLoadingIdeas(false);
    }
  }, [filterStatus, sort, isSuperAdmin]);

  useEffect(() => { loadReleases(); }, [loadReleases]);
  useEffect(() => { loadIdeas(); }, [loadIdeas]);

  // Auto-mark all releases as viewed on tab open (one POST per unviewed)
  useEffect(() => {
    if (tab !== "releases" || releases.length === 0) return;
    const toMark = releases.filter(r => !viewedReleaseIds.has(r.id));
    if (toMark.length === 0) return;
    Promise.all(toMark.map(r => markReleaseViewed(r.id).catch(() => null))).then(() => {
      setViewedReleaseIds(new Set(releases.map(r => r.id)));
      window.dispatchEvent(new Event("whats-new-read"));
    });
  }, [tab, releases, viewedReleaseIds]);

  // ── Mutations ────────────────────────────────────────────────────────
  const handleMarkRead = async (id) => {
    try { await markReleaseViewed(id); } catch {}
    setViewedReleaseIds(prev => new Set([...prev, id]));
    window.dispatchEvent(new Event("whats-new-read"));
    toast.success("Marked as read");
  };

  const handleToggleVote = async (idea) => {
    // Optimistic
    const prev = ideas;
    setIdeas(prev.map(i => i.id === idea.id ? {
      ...i,
      did_vote: !i.did_vote,
      vote_count: (i.vote_count || 0) + (i.did_vote ? -1 : 1),
    } : i));
    try {
      const fn = idea.did_vote ? unvoteIdea : voteIdea;
      const res = await fn(idea.id);
      setIdeas(curr => curr.map(i => i.id === idea.id ? { ...i, vote_count: res.vote_count, did_vote: res.did_vote } : i));
      if (!idea.did_vote) toast.success("+1 — thanks for the signal!");
    } catch (e) {
      setIdeas(prev);  // rollback
      toast.error(e?.response?.data?.detail || "Vote failed");
    }
  };

  const handleDeleteIdea = async (idea) => {
    if (!window.confirm(`Delete this idea?\n\n"${idea.title}"\n\nThis is permanent — all +1s are removed.`)) return;
    try {
      await adminDeleteIdea(idea.id);
      setIdeas(curr => curr.filter(i => i.id !== idea.id));
      toast.success("Idea deleted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const handleApproveIdea = async (idea) => {
    try {
      const updated = await adminUpdateIdea(idea.id, { status: "submitted" });
      setIdeas(curr => curr.map(i => i.id === idea.id ? { ...i, ...updated } : i));
      toast.success("Idea approved and published");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Approval failed");
    }
  };

  const handleEditSaved = (updated) => {
    setIdeas(curr => curr.map(i => i.id === updated.id ? { ...i, ...updated } : i));
  };

  // ── Submit-idea form state ───────────────────────────────────────────
  const [submitForm, setSubmitForm] = useState({ title: "", description: "", category: "leads" });
  const [submitting, setSubmitting] = useState(false);
  const handleSubmitIdea = async () => {
    if ((submitForm.title || "").trim().length < 4) { toast.error("Title is too short"); return; }
    if ((submitForm.description || "").trim().length < 10) { toast.error("Description should be at least 10 characters"); return; }
    setSubmitting(true);
    try {
      await createIdea(submitForm);
      toast.success("Submitted! It will appear once a super admin approves it.");
      setSubmitForm({ title: "", description: "", category: "leads" });
      setSubmitOpen(false);
      // Super admin will see it instantly via include_pending=true; others won't see until approved
      loadIdeas();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  // ── Derived ──────────────────────────────────────────────────────────
  const sortedReleases = useMemo(() => {
    return [...releases].sort((a, b) => {
      if (a.pinned !== b.pinned) return a.pinned ? -1 : 1;
      return new Date(b.published_at) - new Date(a.published_at);
    });
  }, [releases]);

  const unreadCount = useMemo(() => releases.filter(r => !viewedReleaseIds.has(r.id)).length, [releases, viewedReleaseIds]);

  const pendingCount = useMemo(() => ideas.filter(i => i.status === "pending_approval").length, [ideas]);

  return (
    <div className="space-y-5" data-testid="whats-new-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-900 tracking-tight" style={{ fontFamily: "Manrope" }}>
          What's New
        </h1>
        <p className="text-sm text-gray-600 mt-1">
          See what we've shipped, and tell us what you'd like next. Ideas are visible across every FourPaw IQ customer — top votes drive our roadmap.
        </p>
      </div>

      {/* Tab bar */}
      <div className="flex items-center border-b border-gray-200">
        <button
          onClick={() => setTab("releases")}
          className={`relative px-5 py-3 text-sm font-bold transition-colors ${tab === "releases" ? "text-cyan-700" : "text-gray-500 hover:text-gray-700"}`}
          data-testid="tab-releases"
        >
          <span className="inline-flex items-center gap-2">
            <Sparkles className="h-4 w-4" />
            What's New
            {unreadCount > 0 && (
              <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-cyan-600 text-white text-[10px] font-bold">
                {unreadCount}
              </span>
            )}
          </span>
          {tab === "releases" && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-cyan-500" />}
        </button>
        <button
          onClick={() => setTab("ideas")}
          className={`relative px-5 py-3 text-sm font-bold transition-colors ${tab === "ideas" ? "text-cyan-700" : "text-gray-500 hover:text-gray-700"}`}
          data-testid="tab-ideas"
        >
          <span className="inline-flex items-center gap-2">
            <Lightbulb className="h-4 w-4" />
            Ideas Board
            <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-gray-200 text-gray-700 text-[10px] font-bold">
              {ideas.length}
            </span>
            {isSuperAdmin && pendingCount > 0 && (
              <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-orange-500 text-white text-[10px] font-bold" title="Pending approval">
                {pendingCount} pending
              </span>
            )}
          </span>
          {tab === "ideas" && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-cyan-500" />}
        </button>
      </div>

      {/* RELEASES */}
      {tab === "releases" && (
        <div className="space-y-4 max-w-3xl">
          {loadingReleases ? (
            <div className="py-12 text-center text-sm text-gray-500"><Loader2 className="h-5 w-5 animate-spin inline mr-2" />Loading…</div>
          ) : sortedReleases.length === 0 ? (
            <Card className="p-8 text-center text-sm text-gray-500 bg-gray-50">No releases yet.</Card>
          ) : (
            sortedReleases.map(r => (
              <ReleaseCard key={r.id} release={r} isUnread={!viewedReleaseIds.has(r.id)} onMarkRead={handleMarkRead} />
            ))
          )}
        </div>
      )}

      {/* IDEAS */}
      {tab === "ideas" && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-3 justify-between">
            <div className="flex flex-wrap items-center gap-2">
              <Filter className="h-4 w-4 text-gray-400" />
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="h-8 w-[160px] text-xs" data-testid="idea-status-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  {isSuperAdmin && <SelectItem value="pending_approval">Pending review</SelectItem>}
                  <SelectItem value="submitted">Submitted</SelectItem>
                  <SelectItem value="planned">Planned</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="shipped">Shipped</SelectItem>
                  <SelectItem value="declined">Declined</SelectItem>
                </SelectContent>
              </Select>
              <Select value={sort} onValueChange={setSort}>
                <SelectTrigger className="h-8 w-[120px] text-xs" data-testid="idea-sort-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="top">Top voted</SelectItem>
                  <SelectItem value="new">Newest</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={() => setSubmitOpen(true)} className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white h-9" data-testid="submit-idea-btn">
              <Plus className="h-4 w-4 mr-1.5" /> Submit Idea
            </Button>
          </div>

          {loadingIdeas ? (
            <div className="py-12 text-center text-sm text-gray-500"><Loader2 className="h-5 w-5 animate-spin inline mr-2" />Loading…</div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {ideas.map(i => (
                <IdeaCard
                  key={i.id}
                  idea={i}
                  onToggleVote={handleToggleVote}
                  canModerate={isSuperAdmin}
                  onEdit={setEditingIdea}
                  onDelete={handleDeleteIdea}
                  onApprove={handleApproveIdea}
                />
              ))}
              {ideas.length === 0 && (
                <Card className="col-span-full p-8 text-center text-sm text-gray-500 bg-gray-50">
                  No ideas yet — be the first to submit one!
                </Card>
              )}
            </div>
          )}

          <div className="mt-4 px-4 py-3 rounded-lg bg-blue-50 border border-blue-200 text-xs text-blue-800">
            <strong>Heads up:</strong> ideas are reviewed by the FourPaw IQ team before going public. Posts with profanity or test content are removed.
          </div>
        </div>
      )}

      {/* SUBMIT IDEA MODAL */}
      <Dialog open={submitOpen} onOpenChange={setSubmitOpen}>
        <DialogContent className="max-w-lg" data-testid="submit-idea-dialog">
          <DialogHeader>
            <DialogTitle>Suggest an idea</DialogTitle>
            <DialogDescription>
              Share what would make FourPaw IQ more useful for your team. Other customers can +1 your idea — top votes drive our roadmap.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label htmlFor="idea-title">Title</Label>
              <Input id="idea-title" value={submitForm.title} onChange={(e) => setSubmitForm(f => ({ ...f, title: e.target.value }))} placeholder="e.g. Bulk re-assign leads to a different staff member" data-testid="idea-title-input" />
            </div>
            <div>
              <Label htmlFor="idea-category">Category</Label>
              <Select value={submitForm.category} onValueChange={(v) => setSubmitForm(f => ({ ...f, category: v }))}>
                <SelectTrigger id="idea-category" data-testid="idea-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {IDEA_CATEGORIES.map(c => <SelectItem key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="idea-desc">What problem are you trying to solve?</Label>
              <Textarea
                id="idea-desc"
                rows={4}
                value={submitForm.description}
                onChange={(e) => setSubmitForm(f => ({ ...f, description: e.target.value }))}
                placeholder="A clear before/after helps. Example: 'When a staff member leaves, I have to reassign every lead manually — would love a bulk re-assign tool on the Leads page.'"
                data-testid="idea-desc-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSubmitOpen(false)}>Cancel</Button>
            <Button onClick={handleSubmitIdea} disabled={submitting} className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white" data-testid="idea-submit-confirm">
              {submitting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Submit for review
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* EDIT IDEA DIALOG */}
      <EditIdeaDialog
        idea={editingIdea}
        open={!!editingIdea}
        onClose={() => setEditingIdea(null)}
        onSaved={handleEditSaved}
      />
    </div>
  );
}
